import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader, Surface } from "@/components/Layout";
import { api } from "@/lib/api";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDelta = (v) => {
  const n = Number(v || 0);
  const abs = fmt$(Math.abs(n));
  if (n < 0) return `-${abs}`;
  if (n > 0) return `+${abs}`;
  return abs;
};

export default function BalanceFunding() {
  const [items, setItems] = useState([]);
  const [merchantId, setMerchantId] = useState("");

  const load = useCallback(async () => {
    const q = merchantId ? `?merchant_id=${encodeURIComponent(merchantId)}` : "";
    const r = await api.get(`/admin/balance-funding${q}`);
    setItems(r.data.items ?? []);
  }, [merchantId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="balance-funding-page">
        <PageHeader title="Merchant balance funding" subtitle="All admin top-ups across merchants" />
        <div className="mb-4 flex flex-wrap gap-2 items-end">
          <label className="text-sm">
            <span className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Filter by merchant ID</span>
            <input
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              placeholder="Optional merchant id"
              className="h-9 px-3 border border-zinc-200 rounded-sm text-sm w-72 font-mono"
            />
          </label>
          <button type="button" onClick={load} className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800">
            Apply
          </button>
        </div>
        <Surface className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Merchant</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Balance after</th>
                <th className="px-5 py-3">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs text-zinc-500">{(f.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-5 py-3">
                    <Link to={`/merchants/${f.merchant_id}`} className="font-medium hover:underline">
                      {f.merchant?.company_name || f.merchant_id}
                    </Link>
                    <div className="text-[10px] text-zinc-500 font-mono">{f.merchant_id}</div>
                  </td>
                  <td className={`px-5 py-3 text-right font-mono tabular-nums ${Number(f.amount) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {fmtDelta(f.amount)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(f.balance_after)}</td>
                  <td className="px-5 py-3 text-xs text-zinc-600">{f.note || "—"}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-xs text-zinc-500">No funding records.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Surface>
      </div>
    </Layout>
  );
}
